/**
 * Базовый парсер формул из JSON Schema
 * Поддерживает простые операции: SUM, COUNT, MAX, MIN, WHERE
 */

import { getRoot } from 'mobx-state-tree';

/**
 * Находит содержимое скобок с учетом вложенности
 * @param {string} str - строка для поиска
 * @param {number} startIndex - индекс открывающей скобки
 * @returns {string|null} содержимое скобок или null если не найдено
 */
function extractNestedParentheses(str, startIndex) {
    let depth = 0;
    let endIndex = -1;

    for (let i = startIndex; i < str.length; i++) {
        if (str[i] === '(') {
            depth++;
        } else if (str[i] === ')') {
            depth--;
            if (depth === 0) {
                endIndex = i;
                break;
            }
        }
    }

    if (endIndex === -1) return null;
    return str.substring(startIndex + 1, endIndex);
}

/**
 * Парсинг и выполнение формулы
 * @param {string} formula - формула из x-formula
 * @param {object} context - контекст с данными (self из MST)
 * @returns {any} результат вычисления
 */
export function parseFormula(formula, context) {
    if (!formula || !context) return 0;

    try {
        // Заменяем переменные на значения из контекста
        let processedFormula = formula;

        // Обработка $root - получаем корневую модель
        let root = null;
        if (formula.includes('$root')) {
            root = getRoot(context);
            // Заменяем $root на обращение к переменной root
            processedFormula = processedFormula.replace(/\$root\./g, 'root.');
        }

        // Обработка функций агрегации с учетом вложенных скобок
        // SUM
        let sumIndex = processedFormula.indexOf('SUM(');
        while (sumIndex !== -1) {
            const content = extractNestedParentheses(processedFormula, sumIndex + 3);
            if (content !== null) {
                const value = sumPath(context, content);
                const endIndex = sumIndex + 4 + content.length + 1;
                processedFormula = processedFormula.substring(0, sumIndex) + value + processedFormula.substring(endIndex);
                sumIndex = processedFormula.indexOf('SUM(', sumIndex);
            } else {
                break;
            }
        }
        
        // COUNT
        let countIndex = processedFormula.indexOf('COUNT(');
        while (countIndex !== -1) {
            const content = extractNestedParentheses(processedFormula, countIndex + 5);
            if (content !== null) {
                const value = countPath(context, content);
                const endIndex = countIndex + 6 + content.length + 1;
                processedFormula = processedFormula.substring(0, countIndex) + value + processedFormula.substring(endIndex);
                countIndex = processedFormula.indexOf('COUNT(', countIndex);
            } else {
                break;
            }
        }
        
        // MAX
        let maxIndex = processedFormula.indexOf('MAX(');
        while (maxIndex !== -1) {
            const content = extractNestedParentheses(processedFormula, maxIndex + 3);
            if (content !== null) {
                const value = maxPath(context, content);
                const endIndex = maxIndex + 4 + content.length + 1;
                processedFormula = processedFormula.substring(0, maxIndex) + value + processedFormula.substring(endIndex);
                maxIndex = processedFormula.indexOf('MAX(', maxIndex);
            } else {
                break;
            }
        }
        
        // MIN
        let minIndex = processedFormula.indexOf('MIN(');
        while (minIndex !== -1) {
            const content = extractNestedParentheses(processedFormula, minIndex + 3);
            if (content !== null) {
                const value = minPath(context, content);
                const endIndex = minIndex + 4 + content.length + 1;
                processedFormula = processedFormula.substring(0, minIndex) + value + processedFormula.substring(endIndex);
                minIndex = processedFormula.indexOf('MIN(', minIndex);
            } else {
                break;
            }
        }
        
        // Обработка WHERE условий
        processedFormula = processedFormula.replace(
            /([\w.[\]]+)\.WHERE\((.*?)\)/g,
            (match, collection, condition) => {
                const filtered = filterCollection(context, collection, condition);
                return JSON.stringify(filtered);
            }
        );
        
        // Сначала обрабатываем обращения к вложенным полям (например, body.totalHours)
        // Исключаем числа с десятичной точкой (например, 0.2) и Math функции
        // Обрабатываем вложенные пути (например, content.supportDetails.months)
        processedFormula = processedFormula.replace(
            /\b([a-zA-Z_]\w*)((?:\.\w+)+)\b/g,
            (match, obj, fieldPath) => {
                // Не трогаем Math функции
                if (obj === 'Math') {
                    return match;
                }

                // Удаляем начальную точку и разбиваем на части
                const fields = fieldPath.substring(1).split('.');

                // Специальная обработка для root
                if (obj === 'root' && root) {
                    let value = root;
                    for (const field of fields) {
                        if (value && typeof value === 'object' && field in value) {
                            value = value[field];
                        } else {
                            return '0'; // Если любая часть пути не найдена, возвращаем 0
                        }
                    }
                    if (value !== undefined) {
                        return typeof value === 'string' ? `"${value}"` : value;
                    }
                }

                if (obj in context && typeof context[obj] === 'object' && context[obj] !== null) {
                    let value = context[obj];
                    // Проходим по всем частям пути
                    for (const field of fields) {
                        if (value && typeof value === 'object' && field in value) {
                            value = value[field];
                        } else {
                            return '0'; // Если любая часть пути не найдена, возвращаем 0
                        }
                    }
                    if (value !== undefined) {
                        return typeof value === 'string' ? `"${value}"` : value;
                    }
                }
                // Если объект не найден, возвращаем 0
                return '0';
            }
        );

        // Заменяем переменные на значения, но не трогаем части путей (перед точкой) и встроенные объекты
        processedFormula = processedFormula.replace(
            /\b([a-zA-Z_]\w*)\b(?!\.)/g,
            (match) => {
                // Не трогаем встроенные объекты JavaScript и root
                if (['Math', 'Date', 'JSON', 'Number', 'String', 'Boolean', 'Array', 'Object', 'root'].includes(match)) {
                    return match;
                }
                if (match in context && typeof context[match] !== 'function') {
                    const value = context[match];
                    // Для объектов оставляем как есть - они часть пути
                    if (typeof value === 'object' && value !== null) {
                        return match;
                    }
                    return typeof value === 'string' ? `"${value}"` : value;
                }
                return match;
            }
        );

        console.log('Original formula:', formula);
        console.log('Processed formula:', processedFormula);
        console.log('Context:', context);
        if (root) {
            console.log('Root object:', root);
            console.log('Root.hourRate:', root.hourRate);
        }

        // Проверяем на синтаксические ошибки в обработанной формуле
        // Ищем паттерны вида "число(" которые вызывают ошибку "is not a function"
        if (/\d+\(/.test(processedFormula)) {
            console.error('Formula has number followed by parenthesis:', processedFormula);
            // Пытаемся исправить, добавив пробел
            processedFormula = processedFormula.replace(/(\d+)(\()/g, '$1 * $2');
        }

        // Безопасное выполнение формулы
        // Используем Function вместо eval для большей безопасности
        // Передаем root если он есть
        const func = root
            ? new Function('Math', 'root', `return ${processedFormula}`)
            : new Function('Math', `return ${processedFormula}`);
        const result = root ? func(Math, root) : func(Math);

        console.log('Formula result:', result);
        return result;
    } catch (error) {
        console.error('Formula parsing error:', error, 'Formula:', formula);
        return 0;
    }
}

/**
 * Вспомогательные функции для работы с путями
 */

function getValueByPath(obj, path) {
    console.log(`[getValueByPath] Getting path: "${path}" from object with keys:`, Object.keys(obj || {}));
    // Удаляем WHERE условия для получения базового пути
    const cleanPath = path.split('.WHERE')[0];

    // Разбираем путь типа "calculations[].cost" или "calculations.cost"
    const parts = cleanPath.split(/[\[\].]/).filter(Boolean);
    console.log(`[getValueByPath] Path parts:`, parts);

    let current = obj;
    for (const part of parts) {
        console.log(`[getValueByPath] Getting part "${part}", current type:`, typeof current, 'value:', current);
        if (current == null) return null;
        current = current[part];
    }

    console.log(`[getValueByPath] Final result:`, current);
    return current;
}

function sumPath(context, path) {
    console.log(`[sumPath] Processing path: "${path}"`);
    console.log('[sumPath] Context keys:', Object.keys(context));

    // Обработка рекурсивного поиска вида "content..totalHours"
    if (path.includes('..')) {
        const [basePath, fieldName] = path.split('..');
        console.log(`[sumPath] Recursive search detected. Base path: "${basePath}", Field: "${fieldName}"`);

        const baseObj = basePath ? getValueByPath(context, basePath) : context;
        console.log(`[sumPath] Base object for recursive search:`, baseObj ? Object.keys(baseObj) : 'null');

        const result = sumAllFieldsRecursive(baseObj, fieldName);
        console.log(`[sumPath] Recursive search result: ${result}`);
        return result;
    }

    // Обработка путей вида "content.stages[].works[].hours"
    // Проверяем количество [] в пути для определения уровня вложенности
    const arrayMarkers = (path.match(/\[\]/g) || []).length;

    // Если есть WHERE, обрабатываем отдельно
    if (path.includes('WHERE')) {
        const [collectionPath, rest] = path.split('.WHERE');
        const matchResult = rest.match(/\((.*?)\)\.(.*)/);

        if (!matchResult) {
            console.warn(`[sumPath] Invalid WHERE syntax in path: "${path}"`);
            return 0;
        }

        const [, condition, fieldPath] = matchResult;

        console.log(`[sumPath] Getting collection at path: "${collectionPath}" from context`);
        const collection = getValueByPath(context, collectionPath);
        console.log(`[sumPath] Collection type:`, typeof collection, 'Is array:', Array.isArray(collection), 'Value:', collection);
        if (!Array.isArray(collection)) {
            console.log(`[sumPath] Not an array, returning 0`);
            return 0;
        }

        console.log(`[sumPath] WHERE condition: "${condition}", field to sum: "${fieldPath}"`);
        const filtered = collection.filter(item => evaluateCondition(item, condition));
        console.log(`[sumPath] Filtered ${filtered.length} items from ${collection.length} total`);

        const sum = filtered.reduce((sum, item) => {
            const value = getValueByPath(item, fieldPath);
            return sum + (Number(value) || 0);
        }, 0);

        console.log(`[sumPath] Final sum: ${sum}`);
        return sum;
    }

    // Обработка вложенных массивов
    if (arrayMarkers === 2) {
        // Двойная вложенность: content.stages[].works[].hours
        const parts = path.split(/\[\]\./);
        const [basePath, middlePath, fieldPath] = parts;

        const baseCollection = getValueByPath(context, basePath);
        if (!Array.isArray(baseCollection)) return 0;

        let total = 0;
        for (const item of baseCollection) {
            const nestedCollection = item[middlePath.replace('[]', '')];
            if (Array.isArray(nestedCollection)) {
                for (const nestedItem of nestedCollection) {
                    const value = getValueByPath(nestedItem, fieldPath);
                    total += Number(value) || 0;
                }
            }
        }
        return total;
    }

    // Одиночный массив: calculations[].cost
    if (arrayMarkers === 1) {
        const cleanPath = path.replace(/\[\]/g, '');
        const parts = cleanPath.split('.');
        const fieldName = parts.pop();
        const collectionPath = parts.join('.');

        const collection = collectionPath ? getValueByPath(context, collectionPath) : context;

        if (!Array.isArray(collection)) {
            return Number(getValueByPath(context, path)) || 0;
        }

        return collection.reduce((sum, item) => {
            const value = item[fieldName];
            return sum + (Number(value) || 0);
        }, 0);
    }

    // Простое поле без массивов
    return Number(getValueByPath(context, path)) || 0;
}

function countPath(context, path) {
    // Проверяем количество [] в пути для определения уровня вложенности
    const arrayMarkers = (path.match(/\[\]/g) || []).length;

    // Для WHERE условий
    if (path.includes('WHERE')) {
        const [collectionPath, condition] = path.split('.WHERE');
        const coll = getValueByPath(context, collectionPath);
        if (!Array.isArray(coll)) return 0;

        const conditionStr = condition.match(/\((.*?)\)/)[1];
        return coll.filter(item => evaluateCondition(item, conditionStr)).length;
    }

    // Обработка вложенных массивов
    if (arrayMarkers === 2) {
        // Двойная вложенность: content.stages[].works[].length
        const parts = path.split(/\[\]\./);
        const [basePath, middlePath, fieldPath] = parts;

        const baseCollection = getValueByPath(context, basePath);
        if (!Array.isArray(baseCollection)) return 0;

        let total = 0;
        for (const item of baseCollection) {
            const nestedCollection = item[middlePath.replace('[]', '')];
            if (Array.isArray(nestedCollection)) {
                if (fieldPath === 'length') {
                    total += nestedCollection.length;
                } else {
                    total += nestedCollection.filter(Boolean).length;
                }
            }
        }
        return total;
    }

    // Простой путь
    const collection = getValueByPath(context, path);
    if (!collection) return 0;
    if (Array.isArray(collection)) return collection.length;
    return 1;
}

function maxPath(context, path) {
    // Обработка WHERE
    if (path.includes('WHERE')) {
        const [collectionPath, rest] = path.split('.WHERE');
        const matchResult = rest.match(/\((.*?)\)\.(.*)/);

        if (!matchResult) {
            console.warn(`[maxPath] Invalid WHERE syntax in path: "${path}"`);
            return 0;
        }

        const [, condition, fieldPath] = matchResult;

        const collection = getValueByPath(context, collectionPath);
        if (!Array.isArray(collection)) return 0;
        
        const filtered = collection.filter(item => evaluateCondition(item, condition));
        const values = filtered.map(item => Number(getValueByPath(item, fieldPath)) || 0);
        return values.length > 0 ? Math.max(...values) : 0;
    }
    
    // Простой путь
    const parts = path.replace(/\[\]/g, '').split('.');
    const fieldName = parts.pop();
    const collectionPath = parts.join('.');
    
    const collection = collectionPath ? getValueByPath(context, collectionPath) : context;
    
    if (!Array.isArray(collection)) {
        return Number(getValueByPath(context, path)) || 0;
    }
    
    const values = collection.map(item => Number(item[fieldName]) || 0);
    return values.length > 0 ? Math.max(...values) : 0;
}

function minPath(context, path) {
    // Аналогично maxPath, но с Math.min
    if (path.includes('WHERE')) {
        const [collectionPath, rest] = path.split('.WHERE');
        const matchResult = rest.match(/\((.*?)\)\.(.*)/);

        if (!matchResult) {
            console.warn(`[minPath] Invalid WHERE syntax in path: "${path}"`);
            return 0;
        }

        const [, condition, fieldPath] = matchResult;

        const collection = getValueByPath(context, collectionPath);
        if (!Array.isArray(collection)) return 0;
        
        const filtered = collection.filter(item => evaluateCondition(item, condition));
        const values = filtered.map(item => Number(getValueByPath(item, fieldPath)) || 0);
        return values.length > 0 ? Math.min(...values) : 0;
    }
    
    const parts = path.replace(/\[\]/g, '').split('.');
    const fieldName = parts.pop();
    const collectionPath = parts.join('.');
    
    const collection = collectionPath ? getValueByPath(context, collectionPath) : context;
    
    if (!Array.isArray(collection)) {
        return Number(getValueByPath(context, path)) || 0;
    }
    
    const values = collection.map(item => Number(item[fieldName]) || 0);
    return values.length > 0 ? Math.min(...values) : 0;
}

function filterCollection(context, collectionPath, condition) {
    const collection = getValueByPath(context, collectionPath);
    if (!Array.isArray(collection)) return [];
    
    return collection.filter(item => evaluateCondition(item, condition));
}

function evaluateCondition(item, condition) {
    // Обработка множественных условий через AND
    if (condition.includes(' AND ')) {
        const conditions = condition.split(' AND ');
        return conditions.every(cond => evaluateSingleCondition(item, cond.trim()));
    }

    return evaluateSingleCondition(item, condition);
}

function evaluateSingleCondition(item, condition) {
    // Поддержка !=, ==, =
    // Расширенный паттерн для различных операторов сравнения
    const match = condition.match(/(\w+)\s*(!=|==|=)\s*(.+)/);
    if (!match) return true;

    const [, field, operator, rawValue] = match;

    // Обработка значения (убираем кавычки если есть, парсим boolean)
    let value = rawValue.trim();
    if (value.startsWith("'") && value.endsWith("'")) {
        value = value.slice(1, -1);
    } else if (value.startsWith('"') && value.endsWith('"')) {
        value = value.slice(1, -1);
    } else if (value === 'true') {
        value = true;
    } else if (value === 'false') {
        value = false;
    } else if (!isNaN(value)) {
        value = Number(value);
    }

    // Логирование для отладки условий с isInactive
    if (field === 'isInactive') {
        console.log(`[evaluateSingleCondition] Checking isInactive:`, {
            condition,
            field,
            operator,
            value,
            itemValue: item[field],
            result: operator === '!=' ? item[field] !== value : item[field] === value
        });
    }

    // Применяем оператор
    if (operator === '!=' || operator === '!==') {
        return item[field] !== value;
    } else {
        return item[field] === value;
    }
}

/**
 * Рекурсивный поиск и суммирование всех полей с заданным именем
 * @param {object} obj - объект для поиска
 * @param {string} fieldName - имя поля для суммирования
 * @param {number} sum - текущая сумма (для рекурсии)
 * @returns {number} сумма всех найденных полей
 */
function sumAllFieldsRecursive(obj, fieldName, sum = 0, depth = 0, path = '') {
    if (!obj || typeof obj !== 'object') return sum;

    // Отладка - выводим текущий путь обхода
    if (depth === 0) {
        console.log(`[sumAllFieldsRecursive] Starting search for field: "${fieldName}"`);
        console.log('[sumAllFieldsRecursive] Root object keys:', Object.keys(obj));
    }

    // Если есть искомое поле на текущем уровне
    if (fieldName in obj) {
        const value = obj[fieldName];
        const numValue = Number(value) || 0;
        console.log(`[sumAllFieldsRecursive] Found "${fieldName}" at path "${path || 'root'}", value: ${value}, numeric: ${numValue}`);

        // Преобразуем в число и добавляем к сумме
        if (!isNaN(numValue)) {
            sum += numValue;
            console.log(`[sumAllFieldsRecursive] Added ${numValue} to sum, new total: ${sum}`);
        }
    }

    // Рекурсивно обходим все вложенные объекты и массивы
    for (const key in obj) {
        const value = obj[key];
        const currentPath = path ? `${path}.${key}` : key;

        if (Array.isArray(value)) {
            console.log(`[sumAllFieldsRecursive] Entering array at "${currentPath}", length: ${value.length}`);
            // Для массивов обрабатываем каждый элемент
            for (let i = 0; i < value.length; i++) {
                sum = sumAllFieldsRecursive(value[i], fieldName, sum, depth + 1, `${currentPath}[${i}]`);
            }
        } else if (typeof value === 'object' && value !== null) {
            // Для объектов рекурсивно вызываем функцию
            sum = sumAllFieldsRecursive(value, fieldName, sum, depth + 1, currentPath);
        }
    }

    // Выводим итоговую сумму на корневом уровне
    if (depth === 0) {
        console.log(`[sumAllFieldsRecursive] Final sum for field "${fieldName}": ${sum}`);
    }

    return sum;
}

/**
 * Создание getter функции для computed поля
 */
export function createComputedGetter(formula) {
    return function() {
        return parseFormula(formula, this);
    };
}